import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { cn } from '@/utils/cn';
import { PulseDot } from '@/components/ui/pulse-dot';
import { useUnreadComunicados } from '@/hooks/use-unread-comunicados';

type Props = { collapsed: boolean; disabled?: boolean | undefined };

const BASE =
  'relative flex h-9 items-center gap-md rounded-lg px-md text-[13px] font-medium transition-colors';
const ACTIVE =
  'text-text-secondary hover:bg-surface-elevated hover:text-text-primary';
const DISABLED = 'text-text-tertiary/40';

function DisabledButton({ collapsed }: { collapsed: boolean }): ReactNode {
  return (
    <span
      className={cn(BASE, DISABLED, collapsed && 'justify-center px-0')}
      title="Comunicados"
      aria-disabled
    >
      <span className="shrink-0">
        <Bell size={16} />
      </span>
      {!collapsed && <span className="flex-1">Comunicados</span>}
    </span>
  );
}

function BellWithDot({ hasUnread }: { hasUnread: boolean }): ReactNode {
  return (
    <span className="relative shrink-0">
      <Bell size={16} />
      {hasUnread && (
        <span className="absolute -right-0.5 -top-0.5">
          <PulseDot />
        </span>
      )}
    </span>
  );
}

function ActiveButton({
  collapsed,
  count,
}: {
  collapsed: boolean;
  count: number;
}): ReactNode {
  const hasUnread = count > 0;
  return (
    <Link
      to="/communications"
      className={cn(BASE, ACTIVE, collapsed && 'justify-center px-0')}
      aria-label={hasUnread ? `Comunicados (${count} sin leer)` : 'Comunicados'}
    >
      <BellWithDot hasUnread={hasUnread} />
      {!collapsed && (
        <span className="flex-1" aria-live="polite">
          Comunicados
        </span>
      )}
    </Link>
  );
}

export function ComunicadosSidebarButton({
  collapsed,
  disabled,
}: Props): ReactNode {
  const { count } = useUnreadComunicados();
  if (disabled) return <DisabledButton collapsed={collapsed} />;
  return <ActiveButton collapsed={collapsed} count={count} />;
}
